import React, {Fragment, useCallback, useContext} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faArrowRightFromBracket,
  faCheckDouble,
  faClockRotateLeft,
  faClone,
  faEdit,
  faMinus,
  faPlus,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import {Draggable} from "react-beautiful-dnd";
import {FormProvider, useForm} from "react-hook-form";

// Utils
import {deepCopyBuilder, FIELD, getPositionOptions} from "../../utils/builder.js";
import {AuthContext} from "../../contexts/auth.js";
import {exists, generateUniqueKey} from "../../utils/helpers.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Components
import ElementOptions from "../../components/ElementOptions.js";
import Element from "../../components/Element.js";

// Style
import {flex, z} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {border, pad, radius, status} from "../../style/components/variables.js";
import {
  Form,
  Button,
  Section,
  Text,
  SubText,
  RelativeWrapper,
  Label,
  Abbr,
  Small,
  Warning,
  Inline
} from "../../style/components/general.js";
import {useToast} from "../../contexts/toast.js";

const RenderChecksheetBuilder = ({
  builder,
  otherBuilder,
  state,
  isState = false,
  setBuilder,
  filterResult,
  activeDroppableId,
  readOnly,
  modifications,
  setTarget,
  setShowModalEdit,
  setShowModalAdd,
  setShowModalDelete,
  setShowModalFormula,
  setShowModalConditional,
  setShowModalElapsed,
  setShowModalSaveTemplate,
  handleBulk,
  cloneElement,
  transferElement,
  setShowModalHelp = () => {},
  children,
  togglePreview,
  setHasToggledOff,
  error,
  editing = false,
  templates,
  toggleOnly = false,
  fieldToggleOnly = false,
  propagateToggle = true,
  allowToggleChild = true,
  inChecksheetHistory = false,
  inTemplateBuilder = false,
  inEventBuilder = false,
  requestToggle,
  toggleOnIcon = faPlus,
  toggleOffIcon = faMinus,
  toggleOnLabel = "Toggle On",
  toggleOffLabel = "Toggle Off",
  toggleDisabledAction,
  showEmptyMessage = false,
  preventToggleRegulatory = false
}) => {
  const {atLeast} = useContext(AuthContext);
  const {facility} = useContext(FacilityNavContext) || {};

  const {addToast} = useToast();

  const form = useForm();

  const getTemplateLabel = id => {
    const filtered = templates ? templates.filter(template => template.id === id) : null;
    if (filtered?.length) return filtered[0].label;
    return "Template";
  };

  const recurseToggleGenerated = (element, byId, setTo) => {
    Object.keys(byId).map(key => {
      const el = byId[key];
      const {condition: elCondition, trueDepends: elTrueDepends, depends: elDepends} = el;
      if (el.element === "field" && el.shared) {
        if (
          elCondition?.list?.some(({depends: condDepends}) => condDepends === element.name) &&
          el.toggle !== setTo
        ) {
          byId[el.name].toggle = setTo;
          recurseToggleGenerated(byId[el.name], byId, setTo);
        }
        if (elTrueDepends?.includes(element.name) && el.toggle !== setTo) {
          byId[el.name].toggle = setTo;
          recurseToggleGenerated(byId[el.name], byId, setTo);
        }
        if (elDepends?.includes(element.name) && el.toggle !== setTo) {
          byId[el.name].toggle = setTo;
          recurseToggleGenerated(byId[el.name], byId, setTo);
        }
      }
    });
  };

  const recurseInsertParents = (name, byId, setTo) => {
    const {parentName} = byId[name];
    if (parentName) {
      if (parentName in byId) {
        if (!byId[parentName].children.includes(name)) byId[parentName].children.push(name);
        if (byId[parentName].toggle !== setTo) byId[parentName].toggle = setTo;
      } else if (
        !(parentName in byId) &&
        facility?.builder?.byId &&
        parentName in facility.builder.byId
      ) {
        byId[parentName] = {
          ...facility.builder.byId[parentName],
          children: [name],
          toggle: setTo
        };
      }
      recurseInsertParents(parentName, byId, setTo);
    }
  };

  const recurseToggleDeps = (element, byId, setTo, hasNewElements = []) => {
    if (facility) {
      const {condition: elCondition, trueDepends: elTrueDepends, depends: elDepends} = element;

      elCondition?.list
        ?.filter(({depends}) => depends in byId || depends in facility.builder.byId)
        ?.map(({depends}) => {
          if (depends in byId) byId[depends].toggle = setTo;
          else {
            byId[depends] = {...facility.builder.byId[depends], toggle: setTo};
            if (propagateToggle) recurseInsertParents(depends, byId, setTo);
            hasNewElements.push(depends);
          }
          recurseToggleDeps(byId[depends], byId, setTo, hasNewElements);
        });

      elDepends
        ?.filter(depends => depends in byId || depends in facility.builder.byId)
        ?.map(depends => {
          if (depends in byId) byId[depends].toggle = setTo;
          else {
            byId[depends] = {...facility.builder.byId[depends], toggle: setTo};
            if (propagateToggle) recurseInsertParents(depends, byId, setTo);
            hasNewElements.push(depends);
          }
          recurseToggleDeps(byId[depends], byId, setTo, hasNewElements);
        });

      elTrueDepends
        ?.filter(depends => depends in byId)
        ?.map(depends => {
          if (depends in byId) byId[depends].toggle = setTo;
          else {
            byId[depends] = {...facility.builder.byId[depends], toggle: setTo};
            if (propagateToggle) recurseInsertParents(depends, byId, setTo);
            hasNewElements.push(depends);
          }
          recurseToggleDeps(byId[depends], byId, setTo, hasNewElements);
        });
    }

    return hasNewElements;
  };

  const recurseToggle = (element, byId, setTo) => {
    if (element.element === "field" && element.shared) {
      if (!setTo) recurseToggleGenerated(element, byId, setTo);
      else {
        const newBuilder = deepCopyBuilder(builder);
        const result = recurseToggleDeps(element, requestToggle ? newBuilder.byId : byId, setTo);
        if (requestToggle) {
          newBuilder.byId[element.name].toggle = setTo;
          if (setTo) recurseInsertParents(element.name, newBuilder.byId, setTo);
          requestToggle(newBuilder, result);

          return undefined;
        }
      }
    }
    byId[element.name].toggle = setTo;
    if (!byId[element.name].toggle) byId[element.name].preview = false;
    if (byId[element.name].children.length > 0)
      byId[element.name].children
        .filter(child => child in byId)
        .map(child => {
          byId = recurseToggle(byId[child], byId, setTo);
        });

    if (setTo) recurseInsertParents(element.name, byId, setTo);
    return byId;
  };

  const updatedToggle = element => {
    const temp = deepCopyBuilder(builder);
    const {allIds} = temp;
    let {byId} = temp;
    let hasToggledOff = false;
    byId = recurseToggle(element, byId, !element.toggle || false);

    if (byId) {
      for (let i = 0; i < allIds.length; i++) {
        if (!byId[allIds[i]].toggle) {
          hasToggledOff = true;
          break;
        }
      }

      if (setHasToggledOff) setHasToggledOff(hasToggledOff);

      setBuilder({...temp, byId}, undefined, undefined, editing);
    }
  };

  const handleEdit = element => {
    setShowModalEdit(true);
    setTarget(element);
  };

  const handleAdd = element => {
    setShowModalAdd(true);
    setTarget(element);
  };

  const handleDelete = element => {
    setShowModalDelete(true);
    setTarget(element);
  };

  const handleConditional = element => {
    setShowModalConditional(true);
    setTarget(element);
  };

  const handleFormula = element => {
    setShowModalFormula(element);
    setTarget(element);
  };

  const handleElapsed = element => {
    setShowModalElapsed(element);
    setTarget(element);
  };

  const isTimeFormula = element => {
    if (!element?.formula?.length) return false;
    if (!element.formula[0].value) return false;
    if (builder.byId[element.formula[0].value]?.type === "time") return true;
    return false;
  };

  const showElapsed = useCallback(
    ({type, depends}) =>
      type === "time" || depends?.filter(key => builder.byId[key]?.type === "time")?.length > 0,
    [builder]
  );

  const renderOptions = (target, parentToggledOff = false) => {
    if (preventToggleRegulatory && target?.tag?.toLowerCase() === "regulatory")
      return (
        <Options className="renderOptions" element={target.element}>
          <RelativeWrapper>
            <Option
              type="button"
              title={target.toggle ? toggleOffLabel : toggleOnLabel}
              onClick={() => {}}
              isDisabled>
              <FontAwesomeIcon icon={target.toggle ? toggleOffIcon : toggleOnIcon} />
            </Option>
          </RelativeWrapper>
        </Options>
      );
    if (parentToggledOff && !allowToggleChild) return <ToggleWrapper element={target.element} />;
    if (toggleOnly && fieldToggleOnly && target.element !== FIELD)
      return <ToggleWrapper element={target.element} />;

    if (target.shared && !toggleOnly)
      return (
        <Options className="renderOptions" element={target.element}>
          {target.toggle && (
            <>
              <Option
                data-testid="checksheetBuilder.condition"
                type="button"
                title="Add Condition"
                onClick={
                  facility?.builder?.byId?.[target.name]?.condition?.list?.length
                    ? () => addToast("Must edit facility-wide conditions in Facility Builder")
                    : () => handleConditional(target)
                }
                isDisabled={!!facility?.builder?.byId?.[target.name]?.condition?.list?.length}>
                <FontAwesomeIcon icon={faCheckDouble} />
              </Option>
              <Option
                data-testid="checksheetBuilder.formula"
                type="button"
                title="Add/Edit Formula"
                onClick={() => handleFormula(target)}
                lessPad>
                f<SubText>(x)</SubText>
              </Option>
              <Option
                data-testid="element.edit"
                type="button"
                title="Edit"
                isDisabled
                onClick={() => addToast("Must edit shared fields in Facility Builder")}>
                <FontAwesomeIcon icon={faEdit} />
              </Option>
            </>
          )}
          <Option
            type="button"
            title={target.toggle ? toggleOffLabel : toggleOnLabel}
            onClick={() => updatedToggle(target)}>
            <FontAwesomeIcon icon={target.toggle ? toggleOffIcon : toggleOnIcon} />
          </Option>
        </Options>
      );

    if (target.toggle && atLeast("creator") && !toggleOnly)
      return (
        <Options className="renderOptions" data-testid="element.options" element={target.element}>
          {target.element === "field" &&
            !target.shared &&
            otherBuilder?.byId &&
            (!(target.name in otherBuilder.byId) || otherBuilder.byId[target.name]?.deleted) && (
              <Option
                data-testid="checksheetBuilder.transfer"
                type="button"
                title="Copy to Other State"
                onClick={() => transferElement(target)}>
                <FontAwesomeIcon icon={faArrowRightFromBracket} />
              </Option>
            )}
          {target.element === "field" && showElapsed(target) && (
            <Option
              data-testid="checksheetBuilder.elapsed"
              type="button"
              title="Elapsed Time"
              onClick={() => handleElapsed(target)}>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </Option>
          )}
          {target.element === "field" &&
            !isTimeFormula(target) &&
            ["number", "range", "generated"].includes(target.type) && (
              <Option
                data-testid="checksheetBuilder.formula"
                type="button"
                title="Add/Edit Formula"
                onClick={() => handleFormula(target)}
                lessPad>
                f<SubText>(x)</SubText>
              </Option>
            )}
          {target.element === "field" && (
            <Option
              data-testid="checksheetBuilder.condition"
              type="button"
              title="Add Condition"
              onClick={() => handleConditional(target)}>
              <FontAwesomeIcon icon={faCheckDouble} />
            </Option>
          )}
          {target.type !== "generated" && target.element === "field" && (
            <Option
              data-testid="checksheetBuilder.clone"
              type="button"
              title="Clone"
              onClick={() => cloneElement(target)}>
              <FontAwesomeIcon icon={faClone} />
            </Option>
          )}
          {target.type !== "generated" &&
            (target.element === "field" ||
              target.children?.filter(key => builder.byId[key]?.element === "field").length > 0 ||
              getPositionOptions(builder, target.name).length > 0) && (
              <Option
                data-testid="element.edit"
                type="button"
                title="Edit"
                onClick={() => handleEdit(target)}>
                <FontAwesomeIcon icon={faEdit} />
              </Option>
            )}

          {(atLeast("super") || !target.tag || target.tag.toLowerCase() !== "regulatory") && (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {target.element === FIELD || editing ? (
                <Option
                  data-testid="element.delete"
                  type="button"
                  title="Delete"
                  danger
                  onClick={() => handleDelete(target)}>
                  <FontAwesomeIcon icon={faTrash} />
                </Option>
              ) : (
                <RelativeWrapper>
                  {!inTemplateBuilder && (
                    <Option type="button" title="Toggle Off" onClick={() => updatedToggle(target)}>
                      <FontAwesomeIcon icon={faMinus} />
                    </Option>
                  )}
                </RelativeWrapper>
              )}
            </>
          )}
        </Options>
      );

    return (
      <RelativeWrapper>
        {(toggleOnly || (target.element !== FIELD && (!editing || !target.toggle))) && (
          <Options className="renderOptions">
            <Option
              type="button"
              title={target.toggle ? toggleOffLabel : toggleOnLabel}
              onClick={() => toggleDisabledAction?.(target) || updatedToggle(target)}
              isDisabled={toggleDisabledAction?.(target, false)}
              data-testid="element.toggle">
              <FontAwesomeIcon icon={target.toggle ? toggleOffIcon : toggleOnIcon} />
            </Option>
          </Options>
        )}
      </RelativeWrapper>
    );
  };

  const numOptions = target => {
    if (target.toggle && atLeast("creator")) {
      if (toggleOnly) return 1;
      if (target.element !== "field" && inTemplateBuilder) return 1;
      if (target.element !== "field" && !inTemplateBuilder) return 2;
      if (target.type === "number" || target.type === "range") return 5;
      if (target.type === "generated") return 3;
      return 4;
    }

    return 1;
  };

  const modificationHasAttr = (id, allowAdded, ...attrs) =>
    attrs.some(
      attr =>
        modifications &&
        modifications[id] &&
        typeof modifications[id] !== "string" &&
        modifications[id].operation !== "removed" &&
        attr in modifications[id] &&
        (modifications[id][attr] !== "added" || allowAdded)
    );

  const buildRangeDisplay = (units, min, max, strictMin, strictMax, type) => (
    <div>
      {exists(min) && (
        <RegularText isOld={type === "old"} isNew={type === "new"}>
          {strictMin ? ">" : "≥"} {min} {units}
        </RegularText>
      )}
      {exists(max) && (
        <RegularText isOld={type === "old"} isNew={type === "new"}>
          {strictMax ? "<" : "≤"} {max} {units}
        </RegularText>
      )}
    </div>
  );

  const combineAllIds = () => {
    if (!modifications) return builder.allIds;
    return [
      ...Object.keys(modifications).filter(
        key => modifications[key].operation === "removed" && !modifications[key].parentName
      ),
      ...builder.allIds
    ];
  };

  const combineChildren = id => {
    const itemChildren = builder.byId[id]?.children || [];
    if (!modifications) return itemChildren;
    return [
      ...Object.keys(modifications).filter(
        key => modifications[key].operation === "removed" && modifications[key].parentName === id
      ),
      ...itemChildren
    ];
  };

  const getRenderedElements = (ids, parentToggledOff = false) => {
    if (ids && ids.length > 0) {
      return ids
        .filter(id => builder.byId[id] && (!inTemplateBuilder || !builder.byId[id].shared))
        .map((id, index) => {
          let item = builder.byId[id];
          let removedItem = false;
          let modification = modifications ? modifications[id] : undefined;
          let label = item?.label;

          if (!item && modifications && modifications[id]?.operation === "removed") {
            item = modifications[id];
            removedItem = true;
            modification = "removed";
            ({label} = item);
          } else label = facility?.builder?.byId?.[id]?.label || label;

          if (
            !item ||
            item.type === "weather" ||
            (item.element === FIELD && filterResult && !filterResult[id])
          )
            return null;

          const renderedChildren = item.children
            ? getRenderedElements(
                combineChildren(id),
                item.preview ? false : parentToggledOff || !item.toggle
              )
            : null;

          if (!item.preview)
            return (
              (item.element === FIELD ||
                !filterResult ||
                (renderedChildren && renderedChildren.some(child => !!child))) && (
                <Draggable
                  draggableId={id}
                  index={index}
                  key={item.name}
                  isDragDisabled={
                    !item.toggle ||
                    (inTemplateBuilder && item.element === "component") ||
                    !!filterResult ||
                    readOnly ||
                    toggleOnly ||
                    !!item.stale ||
                    !!item.deleted ||
                    !atLeast("creator")
                  }>
                  {provided => (
                    <Wrapper
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      ref={provided.innerRef}
                      key={id}
                      id={id}
                      adjustMargin={item.parentName === "" || inChecksheetHistory ? 1 : 0}
                      hidden={!!item.stale || !!item.deleted}>
                      <ElementOptions
                        builder={builder}
                        otherBuilder={otherBuilder}
                        state={state}
                        isState={isState}
                        element={{...item, label}}
                        key={id}
                        readOnly={readOnly}
                        inHistory={inChecksheetHistory}
                        renderOptions={
                          readOnly
                            ? () => {}
                            : target =>
                                renderOptions(target, !allowToggleChild ? parentToggledOff : false)
                        }
                        activeDroppableId={activeDroppableId}
                        handleAdd={readOnly ? () => {} : handleAdd}
                        togglePreview={togglePreview}
                        setTarget={setTarget}
                        setShowModalSaveTemplate={setShowModalSaveTemplate}
                        error={error}
                        numOptions={
                          readOnly || (parentToggledOff && !allowToggleChild) ? 0 : numOptions(item)
                        }
                        templateLabel={
                          item.templateId && !readOnly ? getTemplateLabel(item.templateId) : null
                        }
                        disableDrag={!!filterResult || readOnly || toggleOnly}
                        modification={modification}
                        toggleOnly={toggleOnly}>
                        {inChecksheetHistory && (
                          <>
                            {(modificationHasAttr(id, false, "globalPrompt") ||
                              (inChecksheetHistory && item.globalPrompt)) && (
                              <Label bold>Global Prompt</Label>
                            )}

                            {modificationHasAttr(id, false, "globalPrompt") &&
                              modifications[id].globalPrompt && (
                                <Detail isOld>
                                  <DetailLabel bold={!inChecksheetHistory}>
                                    {modifications[id].globalPrompt}
                                  </DetailLabel>
                                </Detail>
                              )}

                            {readOnly && item.globalPrompt && (
                              <Detail
                                isNew={
                                  !removedItem &&
                                  !!modifications &&
                                  !!modifications[id] &&
                                  !!modifications[id].globalPrompt
                                }>
                                <DetailLabel bold={!inChecksheetHistory}>
                                  {item.globalPrompt}
                                </DetailLabel>
                              </Detail>
                            )}

                            {(item.address ||
                              item.help ||
                              item.internalIds ||
                              modificationHasAttr(id, false, "help") ||
                              modificationHasAttr(id, false, "internalIds") ||
                              modificationHasAttr(id, false, "address")) && (
                              <>
                                {(modificationHasAttr(id, false, "help") ||
                                  (inChecksheetHistory &&
                                    item?.help?.filter(
                                      helpItem => !helpItem.id && helpItem.key && helpItem.value
                                    )?.length > 0)) && <Label bold>Help</Label>}

                                {modificationHasAttr(id, false, "help") &&
                                  modifications[id].help
                                    ?.filter(
                                      helpItem => !helpItem.id && helpItem.key && helpItem.value
                                    )
                                    ?.map(({key, value}) => (
                                      <Detail
                                        key={`${id}-help-old-${generateUniqueKey(key)}`}
                                        isOld>
                                        <DetailLabel>
                                          <Abbr title={key}>{key}</Abbr>
                                        </DetailLabel>
                                        <StyledNotes>
                                          <StyledNoteText bold={!inChecksheetHistory}>
                                            {value}
                                          </StyledNoteText>
                                        </StyledNotes>
                                      </Detail>
                                    ))}

                                {readOnly &&
                                  item?.help
                                    ?.filter(helpItem => !helpItem.id)
                                    ?.map(({key, value}) => (
                                      <Detail
                                        key={`${id}-help-${generateUniqueKey(key)}`}
                                        isNew={
                                          !removedItem &&
                                          !!modifications &&
                                          !!modifications[id] &&
                                          !!modifications[id].help
                                        }>
                                        <DetailLabel>
                                          <Abbr title={key}>{key}</Abbr>
                                        </DetailLabel>
                                        <StyledNotes>
                                          <StyledNoteText bold={!inChecksheetHistory}>
                                            {value}
                                          </StyledNoteText>
                                        </StyledNotes>
                                      </Detail>
                                    ))}

                                {(modificationHasAttr(id, false, "internalIds") ||
                                  (inChecksheetHistory &&
                                    item?.internalIds?.filter(({key, value}) => key && value)
                                      ?.length > 0)) && <Label bold>Internal IDs</Label>}

                                {modificationHasAttr(id, false, "internalIds") &&
                                  modifications[id].internalIds?.map(
                                    ({key, value}) =>
                                      key &&
                                      value && (
                                        <Detail
                                          key={`${id}-id-old-${generateUniqueKey(key)}`}
                                          isOld>
                                          <DetailLabel>
                                            <Abbr title={key}>{key}</Abbr>
                                          </DetailLabel>
                                          <StyledNotes>
                                            <StyledNoteText bold={!inChecksheetHistory}>
                                              {value}
                                            </StyledNoteText>
                                          </StyledNotes>
                                        </Detail>
                                      )
                                  )}

                                {item?.internalIds?.map(
                                  ({key, value}) =>
                                    key &&
                                    value && (
                                      <Detail
                                        key={`${id}-id-${generateUniqueKey(key)}`}
                                        isNew={
                                          !removedItem &&
                                          !!modifications &&
                                          !!modifications[id] &&
                                          !!modifications[id].internalIds
                                        }>
                                        <DetailLabel>
                                          <Abbr title={key?.toUpperCase()}>
                                            {key?.toUpperCase()}
                                          </Abbr>
                                        </DetailLabel>
                                        <StyledNotes>
                                          <StyledNoteText bold={!inChecksheetHistory}>
                                            {value}
                                          </StyledNoteText>
                                        </StyledNotes>
                                      </Detail>
                                    )
                                )}

                                {(modificationHasAttr(id, false, "address") ||
                                  (inChecksheetHistory && item?.address)) && (
                                  <Label bold>Address</Label>
                                )}

                                {modificationHasAttr(id, false, "address") &&
                                  modifications[id].address && (
                                    <Detail isOld noFlex>
                                      <Label data-testid="edit.location" bold>
                                        {modifications[id].address.primary && <span>Primary</span>}
                                      </Label>
                                      <Text>
                                        <span>{modifications[id].address.line1}</span>
                                        <br />
                                        {modifications[id].address.line2 &&
                                          modifications[id].address.line2 !== "" && (
                                            <>
                                              <span>{modifications[id].address.line2}</span>
                                              <br />
                                            </>
                                          )}
                                        <span>
                                          {modifications[id].address.city}{" "}
                                          {modifications[id].address.state}
                                          {modifications[id].address.zipCode &&
                                            `, ${modifications[id].address.zipCode}`}
                                        </span>
                                        <br />
                                        <Small>
                                          {modifications[id].address.details &&
                                            modifications[id].address.details.map(
                                              detail =>
                                                Object.keys(detail).length > 0 && (
                                                  <Fragment key={detail.key}>
                                                    {`${detail.key}: ${detail.value}`}
                                                    <br />
                                                  </Fragment>
                                                )
                                            )}
                                        </Small>
                                      </Text>
                                    </Detail>
                                  )}

                                {inChecksheetHistory && item?.address && (
                                  <Detail
                                    isNew={
                                      !removedItem &&
                                      !!modifications &&
                                      !!modifications[id] &&
                                      !!modifications[id].address
                                    }
                                    noFlex>
                                    <Label data-testid="edit.location" bold>
                                      {item.address.primary && <span>Primary</span>}
                                    </Label>
                                    <Text>
                                      <span>{item.address.line1}</span>
                                      <br />
                                      {item.address.line2 && item.address.line2 !== "" && (
                                        <>
                                          <span>{item.address.line2}</span>
                                          <br />
                                        </>
                                      )}
                                      <span>
                                        {item.address.city} {item.address.state}
                                        {item.address.zipCode && `, ${item.address.zipCode}`}
                                      </span>
                                      <br />
                                      <Small>
                                        {item.address.details &&
                                          item.address.details.map(
                                            detail =>
                                              Object.keys(detail).length > 0 && (
                                                <Fragment key={detail.key}>
                                                  {`${detail.key}: ${detail.value}`}
                                                  <br />
                                                </Fragment>
                                              )
                                          )}
                                      </Small>
                                    </Text>
                                  </Detail>
                                )}
                              </>
                            )}

                            {(modificationHasAttr(id, false, "options") ||
                              (item?.options &&
                                ["multiselect", "radio", "dropdown"].includes(item?.type))) && (
                              <>
                                <Label bold>Options</Label>
                                {modificationHasAttr(id, false, "options") && (
                                  <RegularText isOld>
                                    {modifications[id].options
                                      .map(option => option.option || option.value || option)
                                      .join(", ")}
                                  </RegularText>
                                )}

                                <RegularText
                                  isNew={!removedItem && modificationHasAttr(id, true, "options")}>
                                  {item.options
                                    .map(option => option.option || option.value || option)
                                    .join(", ")}
                                </RegularText>
                              </>
                            )}

                            {(modificationHasAttr(
                              id,
                              false,
                              "aMin",
                              "aMax",
                              "strictMin",
                              "strictMax"
                            ) ||
                              item?.hasARange) && (
                              <>
                                <Label bold>Acceptable Range</Label>
                                {modificationHasAttr(
                                  id,
                                  false,
                                  "aMin",
                                  "aMax",
                                  "strictMin",
                                  "strictMax"
                                ) &&
                                  modifications[id].hasARange !== false &&
                                  buildRangeDisplay(
                                    modifications[id].units || item?.units,
                                    exists(modifications[id].aMin)
                                      ? modifications[id].aMin
                                      : item?.aMin,
                                    exists(modifications[id].aMax)
                                      ? modifications[id].aMax
                                      : item?.aMax,
                                    modifications[id].strictMin !== undefined
                                      ? modifications[id].strictMin
                                      : item?.strictMin,
                                    modifications[id].strictMax !== undefined
                                      ? modifications[id].strictMax
                                      : item?.strictMax,
                                    "old"
                                  )}
                                {item?.hasARange &&
                                  buildRangeDisplay(
                                    item.units,
                                    item.aMin,
                                    item.aMax,
                                    item.strictMin,
                                    item.strictMax,
                                    modificationHasAttr(
                                      id,
                                      true,
                                      "aMin",
                                      "aMax",
                                      "strictMin",
                                      "strictMax"
                                    )
                                      ? "new"
                                      : undefined
                                  )}
                              </>
                            )}

                            {(modificationHasAttr(id, false, "alertCondition") ||
                              item?.hasAlert) && (
                              <>
                                <Label bold>Acceptable Values</Label>
                                {modificationHasAttr(id, false, "alertCondition") &&
                                  modifications[id].hasAlert !== false && (
                                    <RegularText isOld>
                                      {Array.isArray(modifications[id].alertCondition)
                                        ? modifications[id].alertCondition.join(", ")
                                        : modifications[id].alertCondition}
                                    </RegularText>
                                  )}
                                {item?.hasAlert && (
                                  <RegularText
                                    isNew={
                                      !removedItem &&
                                      modificationHasAttr(id, true, "alertCondition")
                                    }>
                                    {Array.isArray(item.alertCondition)
                                      ? item.alertCondition.join(", ")
                                      : item.alertCondition}
                                  </RegularText>
                                )}
                              </>
                            )}
                          </>
                        )}

                        {renderedChildren || null}
                      </ElementOptions>
                      {provided.placeholder}
                    </Wrapper>
                  )}
                </Draggable>
              )
            );

          return (
            !item.stale &&
            !item.deleted &&
            (!filterResult ||
              item.element === FIELD ||
              (renderedChildren && renderedChildren.some(child => !!child))) && (
              <Element
                task={{builder}}
                key={id}
                attributes={item}
                readOnly
                renderOptions={renderOptions}
                activeDroppableId={activeDroppableId}
                togglePreview={togglePreview}
                setShowModalHelp={setShowModalHelp}
                setTarget={setTarget}
                freezeComponentState={false}
                preview>
                {renderedChildren || null}
              </Element>
            )
          );
        });
    }
    return null;
  };

  const renderElements = ids => {
    const view = getRenderedElements(ids);
    if (view && view.some(element => !!element)) return view;
    return (
      <Wrapper data-testid="checksheetBuilder.body" adjustMargin={inChecksheetHistory ? 1 : 0}>
        <Builder empty>
          <Message>No Elements Found</Message>
        </Builder>
      </Wrapper>
    );
  };

  return (
    <ChecksheetBuilderSection data-testid="checksheetBuilder.body">
      {builder?.allIds?.length > 0 ? (
        <Wrapper adjustMargin={inChecksheetHistory ? 1 : 0}>
          <FormProvider {...form}>
            <Form>{renderElements(combineAllIds())}</Form>
          </FormProvider>
        </Wrapper>
      ) : (
        <RelativeWrapper>
          {!inEventBuilder && !showEmptyMessage && (
            <Wrapper>
              <Builder empty>
                <Message>Checksheet Elements...</Message>
              </Builder>
            </Wrapper>
          )}
          {showEmptyMessage && (
            <Wrapper>
              <Builder noBorder>
                <Message>No Elements Found</Message>
              </Builder>
            </Wrapper>
          )}
        </RelativeWrapper>
      )}
      {children}
      {!inTemplateBuilder && !inChecksheetHistory && !toggleOnly && (
        <Button type="button" onClick={handleAdd} data-testid="checksheet.addField">
          <FontAwesomeIcon icon={faPlus} /> Field
        </Button>
      )}
      {handleBulk && (
        <>
          <BulkEditSticky>
            <BulkButton type="button" onClick={handleBulk}>
              Save
            </BulkButton>
          </BulkEditSticky>
          <WarningWrapper>
            <Warning>Warning!</Warning>
            <Text quiet>You have one or more unsaved changes!</Text>
          </WarningWrapper>
        </>
      )}
    </ChecksheetBuilderSection>
  );
};

RenderChecksheetBuilder.propTypes = {
  builder: PropTypes.objectOf(PropTypes.any),
  otherBuilder: PropTypes.objectOf(PropTypes.any),
  state: PropTypes.objectOf(PropTypes.any),
  isState: PropTypes.bool,
  setBuilder: PropTypes.func.isRequired,
  activeDroppableId: PropTypes.string,
  readOnly: PropTypes.bool,
  modifications: PropTypes.objectOf(PropTypes.any),
  setTarget: PropTypes.func,
  setShowModalEdit: PropTypes.func,
  setShowModalAdd: PropTypes.func,
  setShowModalDelete: PropTypes.func,
  setShowModalFormula: PropTypes.func,
  setShowModalConditional: PropTypes.func,
  setShowModalElapsed: PropTypes.func,
  setShowModalHelp: PropTypes.func,
  handleBulk: PropTypes.func,
  cloneElement: PropTypes.func,
  transferElement: PropTypes.func,
  children: PropTypes.node,
  setShowModalSaveTemplate: PropTypes.func,
  togglePreview: PropTypes.func,
  setHasToggledOff: PropTypes.func,
  error: PropTypes.arrayOf(PropTypes.string),
  editing: PropTypes.bool,
  toggleOnly: PropTypes.bool,
  fieldToggleOnly: PropTypes.bool,
  propagateToggle: PropTypes.bool,
  allowToggleChild: PropTypes.bool,
  templates: PropTypes.arrayOf(PropTypes.any),
  inChecksheetHistory: PropTypes.bool,
  inTemplateBuilder: PropTypes.bool,
  inEventBuilder: PropTypes.bool,
  filterResult: PropTypes.objectOf(PropTypes.any),
  requestToggle: PropTypes.func,
  toggleOnIcon: PropTypes.objectOf(PropTypes.any),
  toggleOffIcon: PropTypes.objectOf(PropTypes.any),
  toggleOnLabel: PropTypes.string,
  toggleOffLabel: PropTypes.string,
  toggleDisabledAction: PropTypes.func,
  showEmptyMessage: PropTypes.bool,
  preventToggleRegulatory: PropTypes.bool
};

// Style Overrides
const ChecksheetBuilderSection = styled(Section)`
  padding: 0;
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: max-content;

  ${({adjustMargin}) =>
    adjustMargin
      ? css`
          padding-top: ${pad}px;

          :first-child {
            padding-top: 0;
          }
        `
      : css`
          margin-bottom: ${pad * 2}px;
        `}

  ${({hidden}) =>
    hidden &&
    css`
      height: 0;
      opacity: 0;
    `}
`;

const Builder = styled.div`
  min-height: 200px;
  padding: ${pad}px;

  ${({empty, theme}) =>
    empty &&
    css`
      border: ${border} dashed ${theme.primary};
      border-radius: ${radius};
      ${flex("row", "wrap", "center", "center")};
    `}

  ${({noBorder}) =>
    noBorder &&
    css`
      border: none;
      ${flex("row", "wrap", "center", "center")};
    `}
`;

const Message = styled(Text)`
  color: ${({theme}) => theme.primary};
  ${voice.medium};
`;

const Options = styled.div`
  ${flex("row", "nowrap", "center", "center")};
  z-index: ${z("base")};
  visibility: hidden;
  opacity: 0;
  transition: none;
  min-height: ${({element}) => (element === FIELD ? 44 : 38)}px;

  :hover {
    visibility: visible;
    opacity: 1;
  }
`;

const Option = styled(Button)`
  margin: 0 0 0 ${pad}px;
  padding-left: ${({lessPad}) => (lessPad ? 8 : pad)}px;
  font-weight: bold;
  height: 34px;
  width: 36px;
  ${voice.normal}

  ${({isDisabled}) =>
    isDisabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}

  ${({danger}) =>
    danger &&
    css`
      background-color: ${({theme}) => theme.error};
    `}
`;

const Detail = styled.div`
  ${({noFlex}) =>
    !noFlex &&
    css`
      display: flex;
      justify-content: space-between;
    `}

  margin: ${pad}px 0;

  p,
  span {
    color: ${({theme}) => theme.secondary};

    ${({isNew, isOld}) => {
      if (isNew)
        return css`
          color: ${status.success};
        `;

      if (isOld)
        return css`
          color: ${status.error};
          text-decoration: line-through;
        `;

      return "";
    }}
  }
`;

const DetailLabel = styled(Text)`
  width: 25%;
  padding-right: ${pad}px;
  text-overflow: ellipsis;
  overflow: hidden;
  min-width: 80px;

  ${({fullWidth}) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

const StyledNotes = styled.div`
  display: flex;
  align-items: center;
  width: 75%;
`;

const StyledNoteText = styled.span`
  font-weight: 700;
  color: ${({theme}) => theme.secondary};
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;

  &:before {
    content: attr(title);
  }
`;

const RegularText = styled.div`
  text-align: left;
  color: ${({theme}) => theme.secondary};
  ${voice.normal};

  ${({isNew, isOld}) => {
    if (isNew)
      return css`
        color: ${status.success};
      `;

    if (isOld)
      return css`
        color: ${status.error};
        text-decoration: line-through;
      `;

    return "";
  }}
`;

const BulkEditSticky = styled.div`
  bottom: ${pad * 2}px;
  margin-top: ${pad * 2}px;
  display: flex;
  position: sticky;
  justify-content: start;
  z-index: ${z("above")};
  height: 35px;
`;

const WarningWrapper = styled(Inline)`
  position: absolute;
  right: 0;
  bottom: ${pad}px;
  padding: 0 ${pad}px;
  width: 75%;
  align-items: start;
  justify-self: end;
  justify-content: end;
`;

const BulkButton = styled(Button)`
  width: 150px;
  box-shadow: 0 10px 20px rgb(0 0 0 / 50%);
`;

const ToggleWrapper = styled(RelativeWrapper)`
  min-height: ${({element}) => (element === FIELD ? 44 : 38)}px;
`;

export default RenderChecksheetBuilder;
